import { z } from 'zod';

import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

type InvoiceDetailsSchemaOptions = {
    canEditEInvoicing?: boolean;
};

export const InvoiceDetailsSchema = ({ canEditEInvoicing = false }: InvoiceDetailsSchemaOptions = {}) => {
    const eInvoicingDetails = useUserStore.getState().eInvoicingDetails ?? [];
    const schemeMap = Object.fromEntries(
        eInvoicingDetails.map(({ scheme, regex }) => [
            scheme,
            { regex: new RegExp(regex), message: t(`onboarding.invoicingDetails.eInvoicing.invalidFormat.${scheme}`) },
        ])
    );

    return z
        .object({
            email: z
                .string()
                .min(1, t('general.atLeastOneLetter'))
                .regex(/^[\w-.]+@([\w-]+\.)+[\w-]{2,10}$/, t('general.emailIsNotCorrect')),
            vatCountry: z.string().min(1),
            vat: z.string().min(1),
            schemeId: canEditEInvoicing
                ? z
                      .string()
                      .or(z.undefined())
                      .refine(val => val && val.length > 0, t('general.selectScheme'))
                : z.string().or(z.undefined()).optional(),
            participantIdentifier: canEditEInvoicing
                ? z.string().min(1, t('general.atLeastOneLetter'))
                : z.string().optional(),
        })
        .superRefine((data, ctx) => {
            if (!canEditEInvoicing) {
                return;
            }
            const scheme = data.schemeId ? schemeMap[data.schemeId] : undefined;
            if (scheme && !scheme.regex.test(data.participantIdentifier ?? '')) {
                ctx.addIssue({
                    code: 'custom',
                    message: scheme.message,
                    path: ['participantIdentifier'],
                });
            }
        });
};

export type InvoiceDetailsSchemaType = z.infer<ReturnType<typeof InvoiceDetailsSchema>>;
